import React, { useState, useEffect, useRef } from "react";
import { auth, db } from "../firebase/config";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  collection, getDocs, query, where, doc, updateDoc,
  addDoc, onSnapshot, orderBy, serverTimestamp, getDoc
} from "firebase/firestore";

export default function ProgramLeadDashboard() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [scholars, setScholars] = useState([]);
  const [facilitators, setFacilitators] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [announcement, setAnnouncement] = useState("");
  const [announcements, setAnnouncements] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [selectedScholar, setSelectedScholar] = useState(null);
  const [scholarDetails, setScholarDetails] = useState({});
  const [assignModal, setAssignModal] = useState(null);
  const chatEndRef = useRef(null);

  async function handleLogout() {
    await signOut(auth);
    navigate("/login");
  }

  async function fetchData() {
    try {
      const [sSnap, fSnap, pSnap] = await Promise.all([
        getDocs(query(collection(db, "users"), where("role", "==", "scholar"), where("status", "==", "approved"))),
        getDocs(query(collection(db, "users"), where("role", "==", "facilitator"))),
        getDocs(query(collection(db, "users"), where("role", "==", "scholar"), where("status", "==", "pending"))),
      ]);
      setScholars(sSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setFacilitators(fSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setPending(pSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "announcements"), orderBy("createdAt", "desc")),
      snap => setAnnouncements(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "chats", "facilitators-group", "messages"), orderBy("createdAt", "asc")),
      snap => {
        setChatMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      }
    );
    return unsub;
  }, []);

  async function sendAnnouncement() {
    if (!announcement.trim()) return;
    await addDoc(collection(db, "announcements"), {
      message: announcement, createdAt: serverTimestamp(),
      authorId: currentUser.uid, authorName: `${userProfile?.firstName} ${userProfile?.lastName}`,
      role: "program_lead",
    });
    setAnnouncement("");
  }

  async function sendChatMessage() {
    if (!chatInput.trim()) return;
    await addDoc(collection(db, "chats", "facilitators-group", "messages"), {
      text: chatInput, createdAt: serverTimestamp(),
      senderId: currentUser.uid, senderName: `${userProfile?.firstName} ${userProfile?.lastName}`,
      senderRole: "program_lead",
    });
    setChatInput("");
  }

  async function handleApprove(id) {
    await updateDoc(doc(db, "users", id), { status: "approved" });
    fetchData();
  }

  async function handleReject(id) {
    await updateDoc(doc(db, "users", id), { status: "rejected" });
    fetchData();
  }

  async function assignFacilitator(scholarId, facilitatorId) {
    await updateDoc(doc(db, "users", scholarId), { facilitatorId });
    setAssignModal(null);
    fetchData();
  }

  async function viewScholarDetails(scholar) {
    setSelectedScholar(scholar);
    setActiveTab("scholar-detail");
    try {
      const [identity, proofPractice] = await Promise.all([
        getDoc(doc(db, "identityStatements", scholar.id)),
        getDoc(doc(db, "proofPractice", scholar.id)),
      ]);
      const reflections = {};
      for (let i = 1; i <= 5; i++) {
        const ref = await getDoc(doc(db, "reflections", `${scholar.id}_0${i}`));
        if (ref.exists()) reflections[`0${i}`] = ref.data();
      }
      setScholarDetails({
        identity: identity.exists() ? identity.data() : null,
        proofPractice: proofPractice.exists() ? proofPractice.data() : null,
        reflections,
      });
    } catch (err) { console.error(err); }
  }

  const completedScholars = scholars.filter(s => (s.currentSession || 1) >= 5).length;
  const passRate = scholars.length > 0 ? Math.round((completedScholars / scholars.length) * 100) : 0;
  const initials = `${userProfile?.firstName?.[0] || ""}${userProfile?.lastName?.[0] || ""}`;

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "pending", label: `Pending${pending.length > 0 ? ` (${pending.length})` : ""}` },
    { id: "scholars", label: "Scholars" },
    { id: "facilitators", label: "Facilitators" },
    { id: "announcements", label: "Announcements" },
    { id: "chat", label: "Facilitator Chat" },
    { id: "report", label: "LGU Report" },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@900&family=Poppins:wght@600;700&family=Outfit:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Outfit', sans-serif; background: #f5f6fa; }
        .pl-wrap { display: flex; flex-direction: column; min-height: 100vh; }
        .pl-topbar { background: #111; padding: 0 32px; display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgba(255,255,255,0.07); position: sticky; top: 0; z-index: 100; height: 60px; }
        .pl-tabs { background: #fff; border-bottom: 1px solid #eee; padding: 0 32px; display: flex; gap: 4px; overflow-x: auto; }
        .pl-tab { padding: 14px 16px; font-family: 'Outfit', sans-serif; font-size: 13px; font-weight: 600; color: #999; cursor: pointer; border-bottom: 2px solid transparent; white-space: nowrap; background: transparent; border-top: none; border-left: none; border-right: none; }
        .pl-tab.active { color: #111; border-bottom-color: #111; }
        .pl-content { padding: 32px; flex: 1; }
        .pl-card { background: #fff; border-radius: 14px; border: 1px solid #f0f0f0; overflow: hidden; margin-bottom: 20px; }
        .pl-stat { background: #fff; border-radius: 12px; padding: 20px; text-align: center; border: 1px solid #f0f0f0; }
        .pl-input { width: 100%; padding: 12px 16px; border-radius: 10px; border: 1.5px solid #e8e8e8; background: #f5f5f5; font-size: 14px; font-family: 'Outfit', sans-serif; color: #111; }
        .pl-input:focus { border-color: #111; outline: none; background: #fff; }
        .pl-btn { padding: 10px 20px; border-radius: 8px; background: #111; color: #fff; border: none; font-family: 'Outfit', sans-serif; font-size: 13px; font-weight: 700; cursor: pointer; }
        .pl-btn-outline { padding: 10px 20px; border-radius: 8px; background: transparent; color: #111; border: 1.5px solid #e0e0e0; font-family: 'Outfit', sans-serif; font-size: 13px; font-weight: 700; cursor: pointer; }
        .pl-approve { background: #dcfce7; color: #16a34a; border: none; border-radius: 6px; padding: 7px 14px; font-family: 'Outfit', sans-serif; font-size: 11px; font-weight: 700; cursor: pointer; text-transform: uppercase; }
        .pl-reject { background: #fee2e2; color: #dc2626; border: none; border-radius: 6px; padding: 7px 14px; font-family: 'Outfit', sans-serif; font-size: 11px; font-weight: 700; cursor: pointer; text-transform: uppercase; }
        .pl-row { display: flex; align-items: center; gap: 12px; padding: 14px 20px; border-bottom: 1px solid #f5f5f5; }
        .pl-row:last-child { border-bottom: none; }
        .pl-avatar { width: 36px; height: 36px; border-radius: 50%; background: #111; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; color: #fff; flex-shrink: 0; font-family: 'Outfit', sans-serif; }
        .chat-wrap { display: flex; flex-direction: column; height: 500px; }
        .chat-messages { flex: 1; overflow-y: auto; padding: 20px; display: flex; flex-direction: column; gap: 12px; }
        .chat-bubble { max-width: 70%; padding: 10px 14px; border-radius: 12px; }
        .chat-bubble.mine { background: #111; color: #fff; align-self: flex-end; border-bottom-right-radius: 4px; }
        .chat-bubble.theirs { background: #f5f5f5; color: #111; align-self: flex-start; border-bottom-left-radius: 4px; }
        .chat-input-row { padding: 16px 20px; border-top: 1px solid #f0f0f0; display: flex; gap: 10px; }
        .pl-logout { padding: 8px 16px; background: transparent; border: 1px solid rgba(255,255,255,0.2); border-radius: 8px; color: rgba(255,255,255,0.6); font-family: 'Outfit', sans-serif; font-size: 12px; font-weight: 700; cursor: pointer; }
        .pl-logout:hover { border-color: #fff; color: #fff; }
        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 200; }
        .modal-box { background: #fff; border-radius: 16px; padding: 28px; width: 100%; max-width: 480px; }
        @media (max-width: 768px) { .pl-content { padding: 16px; } .pl-tabs { padding: 0 16px; } .pl-topbar { padding: 0 16px; } }
      `}</style>

      <div className="pl-wrap">
        {/* Top Bar */}
        <div className="pl-topbar">
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ fontFamily: "'Nunito', sans-serif", fontSize: 18, fontWeight: 900, color: "#fff", letterSpacing: -0.5 }}>gosecond</div>
            <div style={{ width: 1, height: 28, background: "rgba(255,255,255,0.15)" }} />
            <div>
              <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: 16, fontWeight: 600, color: "#fff" }}>LEAD</span>
              <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: 16, fontWeight: 600, color: "#fff" }}>NOW</span>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 8, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: 1 }}>Program Lead</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: "rgba(255,255,255,0.5)" }}>Welcome, {userProfile?.firstName}</span>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#111", fontFamily: "'Outfit', sans-serif" }}>{initials}</div>
            <button className="pl-logout" onClick={handleLogout}>Sign Out</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="pl-tabs">
          {tabs.map(t => (
            <button key={t.id} className={`pl-tab ${activeTab === t.id || (activeTab === "scholar-detail" && t.id === "scholars") ? "active" : ""}`} onClick={() => setActiveTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="pl-content">

          {/* OVERVIEW */}
          {activeTab === "overview" && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16, marginBottom: 24 }}>
                {[
                  { label: "Scholars", value: scholars.length, color: "#111" },
                  { label: "Facilitators", value: facilitators.length, color: "#111" },
                  { label: "Pending", value: pending.length, color: pending.length > 0 ? "#dc2626" : "#111" },
                  { label: "Completed", value: completedScholars, color: "#111" },
                  { label: "Pass Rate", value: `${passRate}%`, color: "#111" },
                ].map(stat => (
                  <div key={stat.label} className="pl-stat">
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: "#999", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>{stat.label}</div>
                    <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 28, fontWeight: 700, color: stat.color }}>{stat.value}</div>
                  </div>
                ))}
              </div>
              <div className="pl-card">
                <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0" }}>
                  <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 15, fontWeight: 600, color: "#111" }}>Session Completion</div>
                </div>
                <div style={{ padding: 20, display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
                  {["01 Lead NOW", "02 Lead RIGHT", "03 Lead SMART", "04 Lead TOGETHER", "05 Lead FORWARD"].map((s, i) => {
                    const count = scholars.filter(sc => (sc.currentSession || 1) > i + 1).length;
                    const pct = scholars.length > 0 ? Math.round((count / scholars.length) * 100) : 0;
                    return (
                      <div key={s} style={{ background: "#f9f9f9", borderRadius: 10, padding: 14 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                          <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 700, color: "#111" }}>{s}</span>
                          <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 700, color: "#C9A84C" }}>{pct}%</span>
                        </div>
                        <div style={{ background: "#e0e0e0", borderRadius: 999, height: 5 }}>
                          <div style={{ background: "#C9A84C", borderRadius: 999, height: 5, width: `${pct}%` }} />
                        </div>
                        <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 10, color: "#999", marginTop: 4 }}>{count} of {scholars.length}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* PENDING */}
          {activeTab === "pending" && (
            <div className="pl-card">
              <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0" }}>
                <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 15, fontWeight: 600, color: "#111" }}>Pending Approval</div>
              </div>
              {loading ? <div style={{ padding: 40, textAlign: "center", fontFamily: "'Outfit', sans-serif", color: "#999" }}>Loading...</div>
              : pending.length === 0 ? <div style={{ padding: 48, textAlign: "center", fontFamily: "'Outfit', sans-serif", color: "#999" }}>No pending registrations.</div>
              : pending.map(s => (
                <div key={s.id} className="pl-row">
                  <div className="pl-avatar">{s.firstName?.[0]}{s.lastName?.[0]}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 700, color: "#111" }}>{s.firstName} {s.lastName}</div>
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: "#999" }}>{s.email} · {s.school} · {s.role}</div>
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: "#aaa" }}>Birthday: {s.birthday} · Phone: {s.phone || "N/A"} · {s.church || ""}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="pl-approve" onClick={() => handleApprove(s.id)}>Approve</button>
                    <button className="pl-reject" onClick={() => handleReject(s.id)}>Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* SCHOLARS */}
          {activeTab === "scholars" && (
            <div className="pl-card">
              <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0" }}>
                <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 15, fontWeight: 600, color: "#111" }}>All Scholars</div>
              </div>
              {scholars.length === 0 ? <div style={{ padding: 48, textAlign: "center", fontFamily: "'Outfit', sans-serif", color: "#999" }}>No approved scholars yet.</div>
              : scholars.map(s => {
                const faci = facilitators.find(f => f.id === s.facilitatorId);
                return (
                  <div key={s.id} className="pl-row">
                    <div className="pl-avatar">{s.firstName?.[0]}{s.lastName?.[0]}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 700, color: "#111" }}>{s.firstName} {s.lastName}</div>
                      <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: "#999" }}>{s.school} · Session {s.currentSession || 1}</div>
                      <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: faci ? "#16a34a" : "#dc2626" }}>
                        {faci ? `Assigned to: ${faci.firstName} ${faci.lastName}` : "Not yet assigned to a facilitator"}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="pl-btn-outline" style={{ fontSize: 11 }} onClick={() => setAssignModal(s)}>Assign</button>
                      <button className="pl-btn" style={{ fontSize: 11 }} onClick={() => viewScholarDetails(s)}>View</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* SCHOLAR DETAIL — READ ONLY */}
          {activeTab === "scholar-detail" && selectedScholar && (
            <>
              <button className="pl-btn-outline" style={{ marginBottom: 20 }} onClick={() => setActiveTab("scholars")}>← Back to Scholars</button>
              <div className="pl-card" style={{ marginBottom: 16 }}>
                <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 12 }}>
                  <div className="pl-avatar">{selectedScholar.firstName?.[0]}{selectedScholar.lastName?.[0]}</div>
                  <div>
                    <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 16, fontWeight: 600, color: "#111" }}>{selectedScholar.firstName} {selectedScholar.lastName}</div>
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: "#999" }}>{selectedScholar.school} · {selectedScholar.email}</div>
                  </div>
                </div>
              </div>

              {scholarDetails.identity && (
                <div className="pl-card" style={{ marginBottom: 16 }}>
                  <div style={{ padding: "14px 20px", borderBottom: "1px solid #f0f0f0", fontFamily: "'Poppins', sans-serif", fontSize: 14, fontWeight: 600, color: "#111" }}>Identity Statement</div>
                  <div style={{ padding: 20 }}>
                    {Object.entries(scholarDetails.identity.answers || {}).map(([key, val]) => (
                      <div key={key} style={{ marginBottom: 12 }}>
                        <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{key}</div>
                        <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: "#111", lineHeight: 1.6, background: "#f9f9f9", borderRadius: 8, padding: "10px 14px" }}>{val}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {scholarDetails.proofPractice && (
                <div className="pl-card" style={{ marginBottom: 16 }}>
                  <div style={{ padding: "14px 20px", borderBottom: "1px solid #f0f0f0", fontFamily: "'Poppins', sans-serif", fontSize: 14, fontWeight: 600, color: "#111" }}>Proof Practice</div>
                  <div style={{ padding: 20 }}>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase", marginBottom: 4 }}>Daily Practice</div>
                      <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: "#111", background: "#f9f9f9", borderRadius: 8, padding: "10px 14px" }}>{scholarDetails.proofPractice.setup?.dailyPractice}</div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
                      {Array.from({ length: 21 }, (_, i) => i + 1).map(day => {
                        const d = scholarDetails.proofPractice.days?.[day] || {};
                        return (
                          <div key={day} style={{ width: "100%", aspectRatio: "1", borderRadius: 8, background: d.done ? "#111" : "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: d.done ? "#fff" : "#999", fontFamily: "'Outfit', sans-serif" }}>{day}</div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {Object.keys(scholarDetails.reflections || {}).length > 0 && (
                <div className="pl-card">
                  <div style={{ padding: "14px 20px", borderBottom: "1px solid #f0f0f0", fontFamily: "'Poppins', sans-serif", fontSize: 14, fontWeight: 600, color: "#111" }}>Session Reflections</div>
                  <div style={{ padding: 20 }}>
                    {Object.entries(scholarDetails.reflections).map(([session, data]) => (
                      <div key={session} style={{ marginBottom: 20, paddingBottom: 20, borderBottom: "1px solid #f5f5f5" }}>
                        <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, fontWeight: 700, color: "#0038A8", textTransform: "uppercase", marginBottom: 10 }}>Session {session}</div>
                        {Object.entries(data.answers || {}).map(([q, a]) => (
                          <div key={q} style={{ marginBottom: 10 }}>
                            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: "#111", background: "#f9f9f9", borderRadius: 8, padding: "10px 14px", lineHeight: 1.6 }}>{a}</div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* FACILITATORS */}
          {activeTab === "facilitators" && (
            <div className="pl-card">
              <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0" }}>
                <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 15, fontWeight: 600, color: "#111" }}>All Facilitators</div>
              </div>
              {facilitators.length === 0 ? <div style={{ padding: 48, textAlign: "center", fontFamily: "'Outfit', sans-serif", color: "#999" }}>No facilitators yet.</div>
              : facilitators.map(f => {
                const assigned = scholars.filter(s => s.facilitatorId === f.id);
                return (
                  <div key={f.id} className="pl-row">
                    <div className="pl-avatar" style={{ background: "#C9A84C", color: "#111" }}>{f.firstName?.[0]}{f.lastName?.[0]}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 700, color: "#111" }}>{f.firstName} {f.lastName}</div>
                      <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: "#999" }}>{f.email} · {f.church || "Victory Pasig"}</div>
                      <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: "#0038A8" }}>{assigned.length} scholars assigned</div>
                    </div>
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: f.status === "approved" ? "#16a34a" : "#dc2626", fontWeight: 700, background: f.status === "approved" ? "#dcfce7" : "#fee2e2", padding: "3px 10px", borderRadius: 999 }}>
                      {f.status === "approved" ? "Active" : f.status}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ANNOUNCEMENTS */}
          {activeTab === "announcements" && (
            <>
              <div className="pl-card" style={{ marginBottom: 20 }}>
                <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0" }}>
                  <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 15, fontWeight: 600, color: "#111" }}>Send Announcement</div>
                  <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: "#999", marginTop: 2 }}>Visible to all scholars and facilitators on their dashboard.</div>
                </div>
                <div style={{ padding: 20 }}>
                  <textarea className="pl-input" style={{ minHeight: 80, resize: "vertical", marginBottom: 12 }} placeholder="Write your announcement here..." value={announcement} onChange={e => setAnnouncement(e.target.value)} />
                  <button className="pl-btn" onClick={sendAnnouncement}>Send Announcement</button>
                </div>
              </div>
              <div className="pl-card">
                <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0" }}>
                  <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 15, fontWeight: 600, color: "#111" }}>Past Announcements</div>
                </div>
                {announcements.length === 0 ? <div style={{ padding: 40, textAlign: "center", fontFamily: "'Outfit', sans-serif", color: "#999" }}>No announcements yet.</div>
                : announcements.map(a => (
                  <div key={a.id} style={{ padding: "16px 20px", borderBottom: "1px solid #f5f5f5" }}>
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: "#111", lineHeight: 1.7, marginBottom: 6 }}>{a.message}</div>
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: "#aaa" }}>
                      {a.authorName} · {a.createdAt?.toDate?.()?.toLocaleDateString() || "just now"}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* FACILITATOR GROUP CHAT */}
          {activeTab === "chat" && (
            <div className="pl-card chat-wrap">
              <div style={{ padding: "14px 20px", borderBottom: "1px solid #f0f0f0" }}>
                <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 15, fontWeight: 600, color: "#111" }}>Facilitator Group Chat</div>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: "#999" }}>Program Lead + All Facilitators</div>
              </div>
              <div className="chat-messages">
                {chatMessages.length === 0 && <div style={{ textAlign: "center", fontFamily: "'Outfit', sans-serif", color: "#ccc", fontSize: 13, margin: "auto" }}>No messages yet. Start the conversation.</div>}
                {chatMessages.map(msg => {
                  const isMe = msg.senderId === currentUser.uid;
                  return (
                    <div key={msg.id} style={{ alignSelf: isMe ? "flex-end" : "flex-start", maxWidth: "70%" }}>
                      {!isMe && <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 10, color: "#aaa", marginBottom: 3 }}>{msg.senderName}</div>}
                      <div className={`chat-bubble ${isMe ? "mine" : "theirs"}`}>
                        <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14 }}>{msg.text}</div>
                        <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 10, color: isMe ? "rgba(255,255,255,0.5)" : "#aaa", marginTop: 4, textAlign: "right" }}>
                          {msg.createdAt?.toDate?.()?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) || ""}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={chatEndRef} />
              </div>
              <div className="chat-input-row">
                <input className="pl-input" placeholder="Type a message..." value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === "Enter" && sendChatMessage()} style={{ flex: 1 }} />
                <button className="pl-btn" onClick={sendChatMessage}>Send</button>
              </div>
            </div>
          )}

          {/* LGU REPORT */}
          {activeTab === "report" && (
            <div className="pl-card">
              <div style={{ padding: "60px 40px", textAlign: "center" }}>
                <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 18, fontWeight: 600, color: "#111", marginBottom: 8 }}>LGU Report</div>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: "#999" }}>Full report generation coming in the next update.</div>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div style={{ background: "#111", padding: "8px 32px", textAlign: "center", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
          <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 9, color: "rgba(255,255,255,0.2)", letterSpacing: 1, textTransform: "uppercase" }}>© 2026 GOSECOND INC. All rights reserved.</span>
        </div>
      </div>

      {/* Assign Facilitator Modal */}
      {assignModal && (
        <div className="modal-overlay" onClick={() => setAssignModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 16, fontWeight: 600, color: "#111", marginBottom: 6 }}>Assign Facilitator</h3>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: "#888", marginBottom: 20 }}>Assign {assignModal.firstName} {assignModal.lastName} to a facilitator</p>
            {facilitators.map(f => (
              <div key={f.id} onClick={() => assignFacilitator(assignModal.id, f.id)} style={{ padding: "12px 16px", borderRadius: 10, border: `2px solid ${assignModal.facilitatorId === f.id ? "#111" : "#e0e0e0"}`, marginBottom: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
                <div className="pl-avatar" style={{ width: 30, height: 30, fontSize: 11 }}>{f.firstName?.[0]}{f.lastName?.[0]}</div>
                <div>
                  <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 700, color: "#111" }}>{f.firstName} {f.lastName}</div>
                  <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: "#999" }}>{f.email}</div>
                </div>
                {assignModal.facilitatorId === f.id && <span style={{ marginLeft: "auto", fontFamily: "'Outfit', sans-serif", fontSize: 11, color: "#16a34a", fontWeight: 700 }}>Current</span>}
              </div>
            ))}
            <button className="pl-btn-outline" style={{ width: "100%", marginTop: 12 }} onClick={() => setAssignModal(null)}>Cancel</button>
          </div>
        </div>
      )}
    </>
  );
}
